import { inject, Injectable } from '@angular/core';
import { Observable, from, map, of, switchMap } from 'rxjs';
import { SuggestionContext, WorkEvent } from '@trovata/app/features/trovata-ai/suggestions/models/suggestion-card.model';
import { SuggestionFeedResult, SuggestionsProvider } from '@trovata/app/features/trovata-ai/suggestions/services/suggestions-provider';
import { CashSuggestionsProvider } from '@trovata/app/features/trovata-ai/suggestions/services/cash-suggestions.provider';
import { TmsSuggestionsProvider } from '@trovata/app/features/trovata-ai/suggestions/services/tms-suggestions.provider';
import { TmsPresenceService } from '@trovata/app/features/unified-menu/services/tms-presence.service';

/**
 * SuggestionsProviderRouter — the app-wide runtime-routing suggestions provider.
 *
 * `SuggestionsState` is a root singleton, so a route-level provider override
 * would never reach it. Instead the root `SUGGESTIONS_PROVIDER` token resolves
 * to THIS router, which dispatches each call to the right backend based on the
 * `context` the calling surface declared:
 *
 * - `'cash'` → `CashSuggestionsProvider` (empty feed, no network).
 * - `'tms'` / `'unified'` → `TmsSuggestionsProvider` (ATOM `home-suggestions`),
 *   gated on TMS presence (§21): a Cash-only session — decided from the entitled
 *   unified menu — routes to the no-op provider instead, so shared surfaces
 *   (/home, /trovata-ai) never fire a TMS request for a Cash-only user.
 *
 * Cash isolation: on a Cash context this router is a pure pass-through to the
 * no-op Cash provider, so the Cash path is provably unaffected — the only added
 * surface is one runtime branch that returns an empty feed for Cash.
 *
 * Mirrors `AgentsDataProviderRouter` (one root router, runtime backend choice).
 * Spec lock: `documentation/implementation/system/tms-home-ai-suggestions.md`
 * §11 Iteration 3-D + Iteration 9 (Cash-isolation gating).
 */
@Injectable({ providedIn: 'root' })
export class SuggestionsProviderRouter implements SuggestionsProvider {
	private readonly cashProvider: CashSuggestionsProvider = inject(CashSuggestionsProvider);
	private readonly tmsProvider: TmsSuggestionsProvider = inject(TmsSuggestionsProvider);
	private readonly tmsPresence: TmsPresenceService = inject(TmsPresenceService);

	getSuggestions(context: SuggestionContext): Observable<SuggestionFeedResult> {
		return this.resolve(context).pipe(switchMap((provider: SuggestionsProvider) => provider.getSuggestions(context)));
	}

	getWorkEvents(context: SuggestionContext): Observable<WorkEvent[]> {
		return this.resolve(context).pipe(switchMap((provider: SuggestionsProvider) => provider.getWorkEvents(context)));
	}

	/** The ONE routing + presence gate (review fix R13): Cash short-circuits to the no-op
	 *  provider; a TMS context waits for the entitled-menu verdict (cached after the first
	 *  call) before any network is considered.
	 *
	 *  A menu that never arrives makes `hasTms()` REJECT with a TimeoutError, and that
	 *  rejection is deliberately propagated rather than swallowed into the no-op provider.
	 *  Both callers already treat it as a RETRYABLE failure — `SuggestionsState.fetch` keeps
	 *  the previous cards and leaves `loadedAt` null so the next visit retries, and the
	 *  day-events path nulls `dayEventsLoadedAt` the same way. Routing to the no-op provider
	 *  instead would look tidier and be worse: it returns a SUCCESSFUL empty feed, so
	 *  `loadedAt` gets stamped and the emptiness is cached for the whole TTL — blacking out
	 *  the briefing of a TMS user whose menu was merely slow, which is exactly what
	 *  `TmsPresenceService.hasTms()`'s contract forbids ("never cache it as a verdict").
	 *  (An earlier version of this comment claimed a "fail-closed no-op on timeout" that the
	 *  code never implemented — Copilot on PR #4659 was right to flag the mismatch.) */
	private resolve(context: SuggestionContext): Observable<SuggestionsProvider> {
		if (context === 'cash') {
			return of(this.cashProvider);
		}
		return from(this.tmsPresence.hasTms()).pipe(map((hasTms: boolean): SuggestionsProvider => (hasTms ? this.tmsProvider : this.cashProvider)));
	}
}
